import React, { useState } from "react";
import { motion } from "framer-motion";
import { Facebook, Twitter, Linkedin, Instagram, ArrowUpRight, Send, Mail, Phone, MapPin } from "lucide-react";
import { footerData, navLinks, services, brand } from "../mock";

const iconMap = { Facebook, Twitter, Linkedin, Instagram };

export default function Footer() {
  const [email, setEmail] = useState("");
  const [subscribed, setSubscribed] = useState(false);
  const onSubscribe = (e) => {
    e.preventDefault();
    if (email) { setSubscribed(true); setTimeout(() => setSubscribed(false), 2500); setEmail(""); }
  };

  return (
    <footer className="relative pt-24 pb-10 bg-gradient-to-b from-[#5E0000] via-[#73100D] to-[#5E0000] text-white overflow-hidden">
      {/* Subtle inner pattern — stays in background, no content overlap */}
      <div className="absolute inset-0 opacity-[0.08] pointer-events-none" style={{
        backgroundImage: "radial-gradient(rgba(255,255,255,0.5) 1px, transparent 1px)",
        backgroundSize: "24px 24px",
      }} />

      <motion.div
        initial={{ opacity: 0, y: 30 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={{ duration: 0.7 }}
        className="relative max-w-7xl mx-auto px-6 lg:px-10"
      >
        {/* CTA strip */}
        <div className="rounded-3xl border border-white/15 bg-white/5 backdrop-blur px-8 py-8 lg:px-12 lg:py-10 mb-16 flex flex-col lg:flex-row lg:items-center justify-between gap-6">
          <div>
            <h3 className="text-2xl md:text-3xl font-bold text-white">
              Ready to Automate Your Business and Increase Revenue?
            </h3>
            <p className="mt-2 text-white/75 max-w-2xl">
              Book a 30-minute strategy call. We&apos;ll identify your highest-ROI
              automation opportunity and prepare a tailored proposal within 24 hours.
            </p>
          </div>
          <a href="#contact"
             className="shrink-0 inline-flex items-center gap-2 rounded-full bg-white text-[#73100D] hover:bg-[#f3e0e0] font-semibold px-7 h-12 transition-transform duration-200 hover:scale-[1.03]">
            Book a Strategy Call <ArrowUpRight className="w-4 h-4" />
          </a>
        </div>

        {/* Main grid */}
        <div className="grid lg:grid-cols-12 gap-12">
          <div className="lg:col-span-4">
            <div className="flex items-center gap-3">
              <div className="w-13 h-13 rounded-xl bg-white grid place-items-center overflow-hidden border-2 border-white/40 shadow-[0_2px_14px_-4px_rgba(0,0,0,0.3)]" style={{width:"52px",height:"52px"}}>
                <img src={brand.logo} alt="RSP Innovations" className="w-11 h-11 object-contain" />
              </div>
              <div className="leading-tight">
                <div className="text-[17px] font-bold tracking-tight text-white">
                  RSP <span className="text-[#E5C7C8]">Innovations</span>
                </div>
                <div className="text-[9px] uppercase tracking-[0.18em] text-white/60">RSPInnov LLP · Right Solution Path</div>
              </div>
            </div>
            <p className="mt-5 text-white/75 max-w-md leading-relaxed text-sm">
              {footerData.description}
            </p>

            <div className="mt-7 space-y-3">
              <FooterRow icon={Mail} text={footerData.contact.email} />
              <FooterRow icon={Phone} text={footerData.contact.phone} />
              <FooterRow icon={MapPin} text={footerData.contact.address} />
            </div>

            <div className="mt-7 flex items-center gap-3">
              {footerData.social.map((s) => {
                const Icon = iconMap[s.icon];
                return (
                  <a key={s.name} href={s.href} aria-label={s.name}
                    className="w-10 h-10 rounded-full border border-white/20 hover:border-white hover:bg-white hover:text-[#73100D] grid place-items-center text-white/85 transition-all duration-300 hover:scale-110">
                    <Icon className="w-4 h-4" />
                  </a>
                );
              })}
            </div>
          </div>

          <div className="lg:col-span-2">
            <div className="text-xs uppercase tracking-[0.22em] text-[#E5C7C8] mb-5 font-semibold">Company</div>
            <ul className="space-y-3 text-sm">
              {navLinks.map((l) => (
                <li key={l.name}>
                  <a href={l.href} className="text-white/80 hover:text-white inline-flex items-center gap-1.5 group transition-colors">
                    <span>{l.name}</span>
                    <ArrowUpRight className="w-3.5 h-3.5 opacity-0 -translate-x-1 group-hover:opacity-100 group-hover:translate-x-0 transition" />
                  </a>
                </li>
              ))}
            </ul>
          </div>

          <div className="lg:col-span-3">
            <div className="text-xs uppercase tracking-[0.22em] text-[#E5C7C8] mb-5 font-semibold">Services</div>
            <ul className="space-y-3 text-sm">
              {services.slice(0, 6).map((s) => (
                <li key={s.id}>
                  <a href="#services" className="text-white/80 hover:text-white transition-colors">{s.title}</a>
                </li>
              ))}
            </ul>
          </div>

          <div className="lg:col-span-3">
            <div className="text-xs uppercase tracking-[0.22em] text-[#E5C7C8] mb-5 font-semibold">AI Insights Newsletter</div>
            <p className="text-white/75 text-sm leading-relaxed">
              Practical AI playbooks, automation case studies and engineering stories — monthly, no spam.
            </p>
            <form onSubmit={onSubscribe} className="mt-4">
              <div className="flex items-center bg-white/10 border border-white/20 rounded-full p-1.5 pl-5 focus-within:border-white transition">
                <input value={email} onChange={(e) => setEmail(e.target.value)} type="email" required
                  placeholder="you@company.com"
                  className="bg-transparent outline-none text-sm flex-1 text-white placeholder:text-white/50" />
                <button type="submit"
                  className="shrink-0 rounded-full bg-white text-[#73100D] hover:bg-[#f3e0e0] font-semibold text-sm px-4 py-2 transition inline-flex items-center gap-1.5">
                  {subscribed ? "✓ Joined" : (<>Subscribe <Send className="w-3.5 h-3.5" /></>)}
                </button>
              </div>
            </form>
            <div className="mt-6 text-xs text-white/60">Trusted by 85+ businesses across 12 countries.</div>
          </div>
        </div>

        <div className="mt-14 pt-6 border-t border-white/15 flex flex-wrap items-center justify-between gap-4 text-sm text-white/70">
          <div>&copy; {new Date().getFullYear()} RSP Innovations (RSPInnov LLP). All rights reserved.</div>
          <div className="flex items-center gap-6">
            <a href="#" className="hover:text-white transition">Privacy Policy</a>
            <a href="#" className="hover:text-white transition">Terms of Service</a>
            <a href="#" className="hover:text-white transition">Cookies</a>
          </div>
        </div>
      </motion.div>
    </footer>
  );
}

function FooterRow({ icon: Icon, text }) {
  return (
    <div className="flex items-center gap-3 text-sm text-white/80">
      <div className="w-8 h-8 rounded-lg bg-white/10 border border-white/15 grid place-items-center shrink-0">
        <Icon className="w-3.5 h-3.5" />
      </div>
      <span>{text}</span>
    </div>
  );
}
